import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tabs,
  Row,
  Col,
  Statistic,
  Switch,
  message,
  Popconfirm,
  Badge,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  CameraOutlined,
  CarOutlined,
  SettingOutlined
} from '@ant-design/icons';
import './LaneManagement.css';

const { Option } = Select;
const { TabPane } = Tabs;

interface Lane {
  _id: string;
  laneId: string;
  name: string;
  vehicleType: 'motorcycle' | 'car' | 'mixed';
  direction: 'entry' | 'exit' | 'bidirectional';
  location: {
    zone: string;
    floor: string;
    description: string;
  };
  cameras: Array<{
    cameraId: string;
    position: string;
    purpose: string;
    isActive: boolean;
  }>;
  status: 'active' | 'inactive' | 'maintenance' | 'error';
  statistics: {
    totalVehicles: number;
    successfulRecognitions: number;
    failedRecognitions: number;
    avgRecognitionTime: number;
  };
}

interface RecognitionRecord {
  _id: string;
  laneId: string;
  recognition: {
    plateNumber: string;
    confidence: number;
    status: string;
  };
  timing: {
    detectionTime: string;
    processingDuration: number;
  };
  verification: {
    isVerified: boolean;
  };
}

const LaneManagement: React.FC = () => {
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [recognitionRecords, setRecognitionRecords] = useState<RecognitionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLane, setEditingLane] = useState<Lane | null>(null);
  const [engineStatus, setEngineStatus] = useState({
    isRunning: false,
    totalProcessed: 0,
    successRate: 0
  });
  const [activeTab, setActiveTab] = useState('lanes');
  const [form] = Form.useForm();

  useEffect(() => {
    loadLanes();
    loadRecognitionRecords();
    loadEngineStatus();
  }, []);

  const loadLanes = async () => {
    setLoading(true);
    try {
      // 模擬API調用
      const mockLanes: Lane[] = [
        {
          _id: '1',
          laneId: 'ENTRY_CAR_01',
          name: '汽車進入車道1',
          vehicleType: 'car',
          direction: 'entry',
          location: {
            zone: 'entrance',
            floor: 'ground',
            description: '主要汽車進入口'
          },
          cameras: [{
            cameraId: 'CAM_ENTRY_CAR_01',
            position: 'overhead',
            purpose: 'license_plate',
            isActive: true
          }],
          status: 'active',
          statistics: {
            totalVehicles: 1250,
            successfulRecognitions: 1180,
            failedRecognitions: 70,
            avgRecognitionTime: 1200
          }
        },
        {
          _id: '2',
          laneId: 'EXIT_CAR_01',
          name: '汽車離開車道1',
          vehicleType: 'car',
          direction: 'exit',
          location: {
            zone: 'exit',
            floor: 'ground',
            description: '主要汽車離開口'
          },
          cameras: [{
            cameraId: 'CAM_EXIT_CAR_01',
            position: 'overhead',
            purpose: 'license_plate',
            isActive: true
          }],
          status: 'active',
          statistics: {
            totalVehicles: 1200,
            successfulRecognitions: 1150,
            failedRecognitions: 50,
            avgRecognitionTime: 1100
          }
        },
        {
          _id: '3',
          laneId: 'ENTRY_MOTORCYCLE_01',
          name: '機車進入車道1',
          vehicleType: 'motorcycle',
          direction: 'entry',
          location: {
            zone: 'entrance',
            floor: 'ground',
            description: '機車專用進入口'
          },
          cameras: [{
            cameraId: 'CAM_ENTRY_MOTO_01',
            position: 'side',
            purpose: 'license_plate',
            isActive: true
          }],
          status: 'active',
          statistics: {
            totalVehicles: 850,
            successfulRecognitions: 780,
            failedRecognitions: 70,
            avgRecognitionTime: 1500
          }
        },
        {
          _id: '4',
          laneId: 'EXIT_MOTORCYCLE_01',
          name: '機車離開車道1',
          vehicleType: 'motorcycle',
          direction: 'exit',
          location: {
            zone: 'exit',
            floor: 'ground',
            description: '機車專用離開口'
          },
          cameras: [{
            cameraId: 'CAM_EXIT_MOTO_01',
            position: 'side',
            purpose: 'license_plate',
            isActive: false
          }],
          status: 'maintenance',
          statistics: {
            totalVehicles: 820,
            successfulRecognitions: 750,
            failedRecognitions: 70,
            avgRecognitionTime: 1400
          }
        }
      ];
      setLanes(mockLanes);
    } catch (error) {
      message.error('載入車道列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadRecognitionRecords = async () => {
    try {
      // 模擬API調用
      const mockRecords: RecognitionRecord[] = [
        {
          _id: '1',
          laneId: 'ENTRY_CAR_01',
          recognition: {
            plateNumber: 'ABC-1234',
            confidence: 0.95,
            status: 'success'
          },
          timing: {
            detectionTime: new Date().toISOString(),
            processingDuration: 1200
          },
          verification: {
            isVerified: true
          }
        },
        {
          _id: '2',
          laneId: 'ENTRY_MOTORCYCLE_01',
          recognition: {
            plateNumber: 'DEF-567',
            confidence: 0.88,
            status: 'success'
          },
          timing: {
            detectionTime: new Date(Date.now() - 300000).toISOString(),
            processingDuration: 1500
          },
          verification: {
            isVerified: false
          }
        }
      ];
      setRecognitionRecords(mockRecords);
    } catch (error) {
      message.error('載入辨識記錄失敗');
    }
  };

  const loadEngineStatus = async () => {
    try {
      // 模擬API調用
      setEngineStatus({
        isRunning: true,
        totalProcessed: 3250,
        successRate: 0.92
      });
    } catch (error) {
      message.error('載入引擎狀態失敗');
    }
  };

  const handleCreateLane = () => {
    setEditingLane(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleEditLane = (lane: Lane) => {
    setEditingLane(lane);
    form.setFieldsValue(lane);
    setShowModal(true);
  };

  const handleSaveLane = async (values: any) => {
    try {
      if (editingLane) {
        // 更新車道
        message.success('車道更新成功');
      } else {
        // 創建車道
        message.success('車道創建成功');
      }
      setShowModal(false);
      loadLanes();
    } catch (error) {
      message.error('保存車道失敗');
    }
  };

  const handleDeleteLane = async (laneId: string) => {
    try {
      message.success('車道刪除成功');
      loadLanes();
    } catch (error) {
      message.error('刪除車道失敗');
    }
  };

  const handleToggleEngine = async () => {
    try {
      if (engineStatus.isRunning) {
        // 停止引擎
        setEngineStatus(prev => ({ ...prev, isRunning: false }));
        message.success('車牌辨識引擎已停止');
      } else {
        // 啟動引擎
        setEngineStatus(prev => ({ ...prev, isRunning: true }));
        message.success('車牌辨識引擎已啟動');
      }
    } catch (error) {
      message.error('引擎控制失敗');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'default';
      case 'maintenance': return 'orange';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const getVehicleTypeIcon = (type: string) => {
    switch (type) {
      case 'car': return <CarOutlined />;
      case 'motorcycle': return '🏍️';
      case 'mixed': return '🚗🏍️';
      default: return <CarOutlined />;
    }
  };

  const laneColumns = [
    {
      title: '車道ID',
      dataIndex: 'laneId',
      key: 'laneId',
      width: 120,
    },
    {
      title: '車道名稱',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '車輛類型',
      dataIndex: 'vehicleType',
      key: 'vehicleType',
      render: (type: string) => (
        <Space>
          {getVehicleTypeIcon(type)}
          <span>{type === 'car' ? '汽車' : type === 'motorcycle' ? '機車' : '混合'}</span>
        </Space>
      ),
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      render: (direction: string) => (
        <Tag color={direction === 'entry' ? 'green' : direction === 'exit' ? 'blue' : 'purple'}>
          {direction === 'entry' ? '進入' : direction === 'exit' ? '離開' : '雙向'}
        </Tag>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge 
          status={status === 'active' ? 'success' : status === 'error' ? 'error' : 'warning'} 
          text={status === 'active' ? '運行中' : status === 'inactive' ? '停用' : status === 'maintenance' ? '維護中' : '錯誤'}
        />
      ),
    },
    {
      title: '成功率',
      key: 'successRate',
      render: (record: Lane) => {
        const rate = record.statistics.totalVehicles > 0 
          ? (record.statistics.successfulRecognitions / record.statistics.totalVehicles * 100)
          : 0;
        return <Progress percent={Math.round(rate)} size="small" />;
      },
    },
    {
      title: '攝像頭',
      key: 'cameras',
      render: (record: Lane) => (
        <Space>
          <CameraOutlined />
          <span>{record.cameras.filter(c => c.isActive).length}/{record.cameras.length}</span>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (record: Lane) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => {/* 查看詳情 */}}
          />
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEditLane(record)}
          />
          <Popconfirm
            title="確定要刪除這個車道嗎？"
            onConfirm={() => handleDeleteLane(record.laneId)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const recognitionColumns = [
    {
      title: '時間',
      dataIndex: ['timing', 'detectionTime'],
      key: 'detectionTime',
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '車道',
      dataIndex: 'laneId',
      key: 'laneId',
    },
    {
      title: '車牌號碼',
      dataIndex: ['recognition', 'plateNumber'],
      key: 'plateNumber',
      render: (plateNumber: string) => (
        <Tag color="blue">{plateNumber}</Tag>
      ),
    },
    {
      title: '信心度',
      dataIndex: ['recognition', 'confidence'],
      key: 'confidence',
      render: (confidence: number) => (
        <Progress 
          percent={Math.round(confidence * 100)} 
          size="small" 
          status={confidence > 0.9 ? 'success' : confidence > 0.7 ? 'normal' : 'exception'}
        />
      ),
    },
    {
      title: '處理時間',
      dataIndex: ['timing', 'processingDuration'],
      key: 'processingDuration',
      render: (duration: number) => `${duration}ms`,
    },
    {
      title: '狀態',
      dataIndex: ['recognition', 'status'],
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'success' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
          {status === 'success' ? '成功' : status === 'failed' ? '失敗' : '待審核'}
        </Tag>
      ),
    },
    {
      title: '已驗證',
      dataIndex: ['verification', 'isVerified'],
      key: 'isVerified',
      render: (isVerified: boolean) => (
        <Tag color={isVerified ? 'green' : 'orange'}>
          {isVerified ? '已驗證' : '待驗證'}
        </Tag>
      ),
    },
  ];

  return (
    <div className="lane-management">
      <div className="page-header">
        <h1>車道管理系統</h1>
        <Space>
          <Button
            type={engineStatus.isRunning ? "default" : "primary"}
            icon={engineStatus.isRunning ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={handleToggleEngine}
          >
            {engineStatus.isRunning ? '停止引擎' : '啟動引擎'}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateLane}>
            新增車道
          </Button>
        </Space>
      </div>

      {/* 統計卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="總車道數"
              value={lanes.length}
              prefix={<SettingOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="運行中車道"
              value={lanes.filter(l => l.status === 'active').length}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="總處理量"
              value={engineStatus.totalProcessed}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="辨識成功率"
              value={Math.round(engineStatus.successRate * 100)}
              suffix="%"
              valueStyle={{ color: engineStatus.successRate > 0.9 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="車道管理" key="lanes">
            <Table
              columns={laneColumns}
              dataSource={lanes}
              rowKey="_id"
              loading={loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 條記錄`,
              }}
            />
          </TabPane>
          
          <TabPane tab="辨識記錄" key="recognitions">
            <Table
              columns={recognitionColumns}
              dataSource={recognitionRecords}
              rowKey="_id"
              loading={loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 條記錄`,
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 車道編輯模態框 */}
      <Modal
        title={editingLane ? '編輯車道' : '新增車道'}
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveLane}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="laneId"
                label="車道ID"
                rules={[{ required: true, message: '請輸入車道ID' }]}
              >
                <Input placeholder="例：ENTRY_CAR_01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="車道名稱"
                rules={[{ required: true, message: '請輸入車道名稱' }]}
              >
                <Input placeholder="例：汽車進入車道1" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="vehicleType"
                label="車輛類型"
                rules={[{ required: true, message: '請選擇車輛類型' }]}
              >
                <Select placeholder="選擇車輛類型">
                  <Option value="car">汽車</Option>
                  <Option value="motorcycle">機車</Option>
                  <Option value="mixed">混合</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="direction"
                label="車道方向"
                rules={[{ required: true, message: '請選擇車道方向' }]}
              >
                <Select placeholder="選擇車道方向">
                  <Option value="entry">進入</Option>
                  <Option value="exit">離開</Option>
                  <Option value="bidirectional">雙向</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name={['location', 'description']}
            label="位置描述"
          >
            <Input.TextArea placeholder="描述車道位置" rows={3} />
          </Form.Item>

          <Form.Item
            name="status"
            label="狀態"
          >
            <Select placeholder="選擇狀態">
              <Option value="active">運行中</Option>
              <Option value="inactive">停用</Option>
              <Option value="maintenance">維護中</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LaneManagement;